import React, { useState, useEffect } from 'react';
import { Card, Dropdown, Typography, Avatar, Tag, Badge } from 'antd';
import {
  EditOutlined,
  DeleteOutlined,
  GlobalOutlined,
  MoreOutlined,
  EyeOutlined,
  LockOutlined
} from '@ant-design/icons';
import { getFavicon } from '../utils/getFavicon';
import { CATEGORY_LABELS } from '../models/Bookmark';

const { Text } = Typography;

const BookmarkCard = ({ bookmark, onEdit, onDelete, onClick }) => {
  const { 
    title, 
    url, 
    description, 
    category, 
    tags = [], 
    isPrivate = false,
    clickCount = 0,
    lastVisited,
    createdAt,
    favicon
  } = bookmark;
  
  const [iconUrl, setIconUrl] = useState(null);

  useEffect(() => {
    const loadFavicon = async () => {
      try {
        // 优先使用数据库中保存的favicon
        if (favicon) {
          setIconUrl(favicon);
          return;
        }
        
        // 否则获取favicon
        const faviconUrl = await getFavicon(url);
        setIconUrl(faviconUrl);
      } catch (error) {
        console.error('Error loading favicon:', error);
        setIconUrl('/favicon.png');
      }
    };
    loadFavicon();
  }, [url, favicon]);

  const dropdownItems = {
    items: [
      {
        key: 'edit',
        icon: <EditOutlined />,
        label: '编辑',
        onClick: () => onEdit(bookmark)
      },
      {
        key: 'delete',
        icon: <DeleteOutlined />,
        label: '删除',
        danger: true,
        onClick: () => onDelete(bookmark)
      }
    ]
  };

  const handleCardClick = (e) => {
    // 如果点击的是更多按钮、标签或其子元素，不执行卡片点击事件
    if (e.target.closest('.ant-dropdown-trigger') || 
        e.target.closest('.ant-tag') ||
        e.target.closest('.bookmark-meta')) {
      return;
    }
    
    // 使用传入的onClick函数，如果没有则使用默认行为
    if (onClick) {
      onClick(bookmark);
    } else {
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  };

  // 格式化日期
  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const now = new Date();
    const diffInDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));
    
    if (diffInDays === 0) return '今天';
    if (diffInDays === 1) return '昨天';
    if (diffInDays < 7) return `${diffInDays}天前`;
    if (diffInDays < 30) return `${Math.floor(diffInDays / 7)}周前`;
    if (diffInDays < 365) return `${Math.floor(diffInDays / 30)}个月前`;
    return `${Math.floor(diffInDays / 365)}年前`;
  };

  // 获取域名
  const getDomain = (url) => {
    try {
      return new URL(url).hostname.replace('www.', '');
    } catch {
      return url;
    }
  };

  return (
    <Badge.Ribbon 
      text={isPrivate ? "私有" : null} 
      color="red" 
      style={{ display: isPrivate ? 'block' : 'none' }}
    >
      <Card
        className="bookmark-card"
        hoverable
        onClick={handleCardClick}
        size="small"
        extra={
          <Dropdown
            menu={dropdownItems}
            placement="bottomRight"
            trigger={['click']}
            overlayClassName="bookmark-dropdown"
          >
            <MoreOutlined className="more-icon" />
          </Dropdown>
        }
        style={{ height: '100%' }}
      >
        {/* 头部区域 */}
        <div className="bookmark-header" style={{ marginBottom: 8 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Avatar
              src={iconUrl}
              icon={<GlobalOutlined />}
              className="site-icon"
              size={20}
            />
            <div style={{ flex: 1, minWidth: 0 }}>
              <Text 
                strong 
                className="bookmark-title"
                style={{ 
                  fontSize: '14px',
                  display: 'block',
                  lineHeight: '1.4'
                }}
                ellipsis={{ tooltip: title }}
              >
                {title}
              </Text>
              <Text 
                type="secondary" 
                className="bookmark-domain"
                style={{ 
                  fontSize: '12px',
                  display: 'block',
                  lineHeight: '1.2'
                }}
                ellipsis={{ tooltip: url }}
              >
                {getDomain(url)}
              </Text>
            </div>
            {isPrivate && (
              <LockOutlined style={{ color: '#999', fontSize: '12px' }} />
            )}
          </div>
        </div>

        {/* 内容区域 */}
        <div className="bookmark-content" style={{ marginBottom: 8 }}>
          {description && (
            <Text 
              className="bookmark-description" 
              style={{ 
                fontSize: '13px',
                color: '#666',
                display: 'block',
                lineHeight: '1.4'
              }}
              ellipsis={{ rows: 2, tooltip: description }}
            >
              {description}
            </Text>
          )}
          
          {/* 分类标签 */}
          <div style={{ marginTop: 6 }}>
            <Tag 
              size="small" 
              color="blue"
              style={{ fontSize: '11px', lineHeight: '18px' }}
            >
              {CATEGORY_LABELS[category] || category}
            </Tag>
          </div>

          {/* 自定义标签 */}
          {tags && tags.length > 0 && (
            <div style={{ marginTop: 6 }}>
              {tags.slice(0, 3).map((tag, index) => (
                <Tag 
                  key={index} 
                  size="small"
                  style={{ 
                    fontSize: '10px', 
                    lineHeight: '16px',
                    marginBottom: 2
                  }}
                >
                  {tag}
                </Tag>
              ))}
              {tags.length > 3 && (
                <Tag 
                  size="small" 
                  style={{ 
                    fontSize: '10px', 
                    lineHeight: '16px',
                    color: '#999'
                  }}
                >
                  +{tags.length - 3}
                </Tag>
              )}
            </div>
          )}
        </div>

        {/* 底部元信息 */}
        <div 
          className="bookmark-meta" 
          style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            fontSize: '11px',
            color: '#999',
            borderTop: '1px solid #f0f0f0',
            paddingTop: 6,
            marginTop: 8
          }}
        >
          <span>
            <EyeOutlined style={{ marginRight: 4 }} />
            {clickCount || 0}
          </span>
          <span>
            {lastVisited ? `访问${formatDate(lastVisited)}` : `添加${formatDate(createdAt)}`}
          </span>
        </div>
      </Card>
    </Badge.Ribbon>
  );
};

export default BookmarkCard; 